/* @jsx h */
import { defineComponent, h, computed } from "vue";
import {
  DateRangePicker,
  DateRange,
  DateRangeOption,
  useLocalization,
} from "@lemmonhq/uikit";
import {
  SegmentSelect,
  SegmentOption,
} from "@app/vue/components/UIComponents/index";

export interface WeeklyHeatmapFiltersProps {
  filters: {
    dateRange: DateRange;
    metricInput: "revenue" | "count";
    serviceType?: "takeaway" | "delivery" | "dine-in";
    orderType?: "restaurant" | "bar";
  };
  onFiltersChange: (filters: WeeklyHeatmapFiltersProps["filters"]) => void;
}

export const WeeklyHeatmapFilters = defineComponent<WeeklyHeatmapFiltersProps>({
  name: "WeeklyHeatmapFilters",
  props: {
    filters: { type: Object, required: true },
    onFiltersChange: { type: Function, required: true },
  },
  setup(props) {
    const { t } = useLocalization();

    const dateRangeOptions = computed<DateRangeOption[]>(() => [
      { value: "this_week", label: t("reports.revenue.this_week") },
      { value: "this_month", label: t("reports.revenue.this_month") },
      { value: "this_quarter", label: t("reports.revenue.this_quarter") },
      { value: "this_year", label: t("reports.revenue.this_year") },
      { value: "last_12m", label: t("reports.revenue.last_12_months") },
      { value: "custom", label: t("reports.revenue.custom_range") },
    ]);

    const metricOptions = computed<SegmentOption<"revenue" | "count">[]>(() => [
      { value: "revenue", label: t("reports.heatmap.revenue") },
      { value: "count", label: t("reports.heatmap.count") },
    ]);

    const serviceTypeOptions = computed<
      SegmentOption<"takeaway" | "delivery" | "dine-in">[]
    >(() => [
      { value: "takeaway", label: t("reports.heatmap.takeaway") },
      { value: "delivery", label: t("reports.heatmap.delivery") },
      { value: "dine-in", label: t("reports.heatmap.dine_in") },
    ]);

    return () => (
      <div class="flex gap-4 items-center">
        <div class="">
          <label class="form-control">
            <div class="label">
              <span class="label-text  flex justify-center items-center">
                <span class="material-symbols-outlined mr-1">date_range</span>{" "}
                <span>{t("reports.heatmap.date_range")}</span>
              </span>
            </div>
            <DateRangePicker
              value={props.filters.dateRange}
              onChange={(dateRange) =>
                props.onFiltersChange({ ...props.filters, dateRange })
              }
              options={dateRangeOptions.value}
            />
          </label>
        </div>
        <div class="">
          <label class="form-control w-full">
            <div class="label">
              <span class="label-text  flex justify-center items-center">
                <span class="material-symbols-outlined mr-1">bubble_chart</span>{" "}
                <span>{t("reports.heatmap.metric")}</span>
              </span>
            </div>
            <SegmentSelect
              options={metricOptions.value}
              value={props.filters.metricInput}
              onChange={(metricInput) =>
                props.onFiltersChange({ ...props.filters, metricInput })
              }
            />
          </label>
        </div>

        <div class="hidden lg:flex flex-1 gap-4 justify-end">
          <div class="">
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text  flex justify-center items-center">
                  <span class="material-symbols-outlined mr-1">moped</span>{" "}
                  <span>{t("reports.heatmap.service")}</span>
                </span>
              </div>
              <SegmentSelect
                options={serviceTypeOptions.value}
                value={props.filters.serviceType}
                onChange={(serviceType) =>
                  props.onFiltersChange({ ...props.filters, serviceType })
                }
              />
            </label>
          </div>
        </div>
      </div>
    );
  },
});
